import { useState, useRef } from 'react';
import { Leaf, X, Mail, Lock, UserRound, ArrowRight, Check } from 'lucide-react';
import { signIn, signUp } from '../../lib/cloud';
import { requestPasswordReset } from '../../lib/account';
import { useDialogA11y } from '../../lib/useDialogA11y';
import LegalTermsModal, { type LegalDocType } from './LegalTermsModal';

export interface SaveGuestPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  fieldCount: number;
}

export default function SaveGuestPlanModal({
  isOpen,
  onClose,
  onSuccess,
  fieldCount,
}: SaveGuestPlanModalProps) {
  const [authMode, setAuthMode] = useState<'signup' | 'login'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType>('terms');

  const modalRef = useRef<HTMLDivElement>(null);
  useDialogA11y({ open: isOpen, onClose, ref: modalRef });

  if (!isOpen) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (authMode === 'signup' && !acceptedTerms) {
      setError('Du skal acceptere brugerbetingelserne for at oprette en profil.');
      return;
    }

    setBusy(true);
    try {
      if (authMode === 'signup') {
        const result = await signUp(email.trim(), password, name);
        if (result.needsConfirmation) {
          setInfoMessage(
            'Tjek din indbakke for at bekræfte din e-mail. Din plan er gemt midlertidigt i din browser og knyttes automatisk til din konto, så snart du logger ind.',
          );
        } else {
          onSuccess();
          onClose();
        }
      } else {
        await signIn(email.trim(), password);
        onSuccess();
        onClose();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (
        authMode === 'signup' &&
        (msg.toLowerCase().includes('already registered') ||
          msg.toLowerCase().includes('findes allerede') ||
          msg.toLowerCase().includes('already exists'))
      ) {
        setError(
          'Der findes allerede en bruger med denne e-mail. Skift til "Log ind" ovenfor for at gemme planen på din eksisterende profil.',
        );
      } else {
        setError(
          msg ||
            (authMode === 'signup'
              ? 'Profilen kunne ikke oprettes. Prøv igen.'
              : 'Ugyldig e-mail eller adgangskode.'),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError('Indtast venligst din e-mailadresse i feltet ovenfor først.');
      return;
    }
    setError('');
    setInfoMessage('');
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setInfoMessage('Vi har sendt et link til nulstilling af adgangskode til din e-mailadresse.');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Kunne ikke sende link til nulstilling. Kontrollér e-mailadressen.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 12, 7, 0.75)',
        backdropFilter: 'blur(5px)',
        zIndex: 'calc(var(--z-modal) + 50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-plan-heading"
        style={{
          backgroundColor: '#0c160e',
          color: '#f1f5f9',
          border: '1px solid rgba(74, 222, 128, 0.25)',
          borderRadius: '14px',
          width: '100%',
          maxWidth: '440px',
          padding: '28px 24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 30px rgba(34, 197, 94, 0.08)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="Luk vindue"
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={20} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              color: '#4ade80',
              fontWeight: 700,
              fontSize: '1.05rem',
              marginBottom: 6,
            }}
          >
            <Leaf size={18} /> Plotwise
          </div>
          <h2
            id="save-plan-heading"
            style={{ fontSize: '1.35rem', fontWeight: 700, margin: '4px 0 6px', color: '#f8fafc' }}
          >
            {authMode === 'signup' ? 'Gem din vildtagerplan' : 'Log ind og gem plan'}
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '0.86rem', margin: 0, lineHeight: 1.45 }}>
            {authMode === 'signup'
              ? `Opret en gratis profil for at gemme ${fieldCount > 1 ? `dine ${fieldCount} vildtagre` : 'din vildtager'} permanent.`
              : `Log ind på din profil for at knytte ${fieldCount > 1 ? `dine ${fieldCount} vildtagre` : 'din vildtager'} til dit revir.`}
          </p>
        </div>

        {/* Tab switch */}
        <div
          style={{
            display: 'flex',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            borderRadius: '8px',
            padding: '3px',
            marginBottom: 18,
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setAuthMode('signup');
              setError('');
              setInfoMessage('');
            }}
            style={{
              flex: 1,
              padding: '9px 10px',
              minHeight: 40,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              border: 'none',
              background: authMode === 'signup' ? '#1b3b22' : 'transparent',
              color: authMode === 'signup' ? '#ffffff' : '#94a3b8',
              fontWeight: authMode === 'signup' ? 600 : 400,
              fontSize: '0.86rem',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Opret gratis profil
          </button>
          <button
            type="button"
            onClick={() => {
              setAuthMode('login');
              setError('');
              setInfoMessage('');
            }}
            style={{
              flex: 1,
              padding: '9px 10px',
              minHeight: 40,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              border: 'none',
              background: authMode === 'login' ? '#1b3b22' : 'transparent',
              color: authMode === 'login' ? '#ffffff' : '#94a3b8',
              fontWeight: authMode === 'login' ? 600 : 400,
              fontSize: '0.86rem',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Log ind
          </button>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#fca5a5',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '0.84rem',
              marginBottom: 14,
              lineHeight: 1.4,
            }}
          >
            {error}
          </div>
        )}

        {infoMessage && (
          <div
            role="status"
            style={{
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              border: '1px solid rgba(34, 197, 94, 0.35)',
              color: '#86efac',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '0.84rem',
              marginBottom: 14,
              lineHeight: 1.4,
            }}
          >
            {infoMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {authMode === 'signup' && (
            <div>
              <label
                htmlFor="guest-save-name"
                style={{
                  display: 'block',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: '#cbd5e1',
                  marginBottom: 4,
                }}
              >
                Dit navn
              </label>
              <div style={{ position: 'relative' }}>
                <UserRound
                  size={16}
                  style={{ position: 'absolute', left: 12, top: 13, color: '#64748b' }}
                />
                <input
                  id="guest-save-name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="F.eks. Emil Beiner"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '11px 12px 11px 38px',
                    borderRadius: '8px',
                    border: '1px solid #223727',
                    background: '#071009',
                    color: '#f8fafc',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box',
                    minHeight: 44,
                  }}
                />
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="guest-save-email"
              style={{
                display: 'block',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#cbd5e1',
                marginBottom: 4,
              }}
            >
              E-mailadresse
            </label>
            <div style={{ position: 'relative' }}>
              <Mail
                size={16}
                style={{ position: 'absolute', left: 12, top: 13, color: '#64748b' }}
              />
              <input
                id="guest-save-email"
                type="email"
                required
                autoComplete="email"
                placeholder="din@email.dk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 12px 11px 38px',
                  borderRadius: '8px',
                  border: '1px solid #223727',
                  background: '#071009',
                  color: '#f8fafc',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box',
                  minHeight: 44,
                }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 4,
              }}
            >
              <label
                htmlFor="guest-save-password"
                style={{ fontSize: '0.82rem', fontWeight: 600, color: '#cbd5e1' }}
              >
                Adgangskode
              </label>
              {authMode === 'login' && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={busy}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#4ade80',
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  Glemt adgangskode?
                </button>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <Lock
                size={16}
                style={{ position: 'absolute', left: 12, top: 13, color: '#64748b' }}
              />
              <input
                id="guest-save-password"
                type="password"
                required
                minLength={6}
                autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                placeholder={authMode === 'signup' ? 'Mindst 6 tegn' : '••••••••'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 12px 11px 38px',
                  borderRadius: '8px',
                  border: '1px solid #223727',
                  background: '#071009',
                  color: '#f8fafc',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box',
                  minHeight: 44,
                }}
              />
            </div>
          </div>

          {authMode === 'signup' && (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: '0.8rem',
                color: '#94a3b8',
                cursor: 'pointer',
                marginTop: 2,
              }}
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 2, accentColor: '#22c55e' }}
              />
              <span>
                Jeg accepterer{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setLegalDoc('terms');
                    setShowLegalModal(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#4ade80',
                    textDecoration: 'underline',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  brugerbetingelserne
                </button>{' '}
                og{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setLegalDoc('privacy');
                    setShowLegalModal(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#4ade80',
                    textDecoration: 'underline',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  privatlivspolitikken
                </button>
                .
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={busy}
            style={{
              marginTop: 10,
              padding: '12px 16px',
              minHeight: 44,
              backgroundColor: '#1b3b22',
              color: '#ffffff',
              border: '1px solid #2d5a36',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'background-color 0.15s ease',
            }}
          >
            {busy ? (
              <span>Behandler…</span>
            ) : authMode === 'signup' ? (
              <>
                <Check size={17} color="#86efac" />
                <span>Opret profil og gem plan</span>
              </>
            ) : (
              <>
                <span>Log ind og gem plan</span>
                <ArrowRight size={17} />
              </>
            )}
          </button>
        </form>

        <p
          style={{
            marginTop: 16,
            textAlign: 'center',
            fontSize: '0.78rem',
            color: '#64748b',
            lineHeight: 1.4,
          }}
        >
          {authMode === 'signup'
            ? 'Eksisterende revirer overskrives ikke. Planen knyttes direkte til din nye konto.'
            : 'Dine eksisterende revirer bevares 100%. Gæsteplanen tilføjes som et nyt revir.'}
        </p>
      </div>

      <LegalTermsModal
        initialDoc={legalDoc}
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
      />
    </div>
  );
}
